import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Divider,
  CircularProgress,
  Chip,
  Paper,
  Tabs,
  Tab,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
} from '@mui/material';
import { 
  ArrowBack,
  Edit as EditIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
  AccountCircle as AccountCircleIcon,
  Cake as CakeIcon,
} from '@mui/icons-material';
import { UpdateClientDTO } from '../../types/client';
import { User } from '../../types/user.types';
import { Document } from '../../types/document.types';
import { Quote } from '../../types/quote.types';
import { UtilityService, UtilityType } from '../../types/utility.types';
import { Subscriber } from '../../types/marketing.types';
import { getClientUtilityServices } from '../../api/utilityServices';
import { fetchSuppliers } from '../../api/suppliers';
import { subscriberApi } from '../../api/marketing';
import { uploadDocument, deleteDocument } from '../../api/documents';
import { useClientContext } from '../../context/ClientContext';
import PropertyList from '../../components/Property/PropertyList';
import QuoteList from '../../components/Quote/QuoteList';
import DocumentList from '../../components/Document/DocumentList';
import EditClientDialog from '../../components/Client/EditClientDialog';
import ContactsPanel from '../../components/Client/ContactsPanel';
import IssueLOAButton from '../../components/LOA/IssueLOAButton';
import NotesPanel from '../../components/Notes/NotesPanel';
import AddressHistoryModal from '../../components/Client/AddressHistoryModal';
import * as quoteApi from '../../api/quotes';
import toast from 'react-hot-toast';
import axios from '../../api/axiosConfig';


// Format service type in proper case for display
const formatServiceType = (type: UtilityType | string): string => {
  if (!type) return '';
  
  // Handle special cases
  if (type === 'PPA Export Contract') return 'PPA Export Contract';
  if (type === 'EV') return 'EV';
  
  // Convert other types to proper case
  return type.toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const ClientDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedClient: client, loading, error, updateClient, fetchClient } = useClientContext();
  const [tabValue, setTabValue] = React.useState('properties');
  const [filteredDocumentCount, setFilteredDocumentCount] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [services, setServices] = useState<UtilityService[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loadingSubscribers, setLoadingSubscribers] = useState(false);
  const [addressHistoryModalOpen, setAddressHistoryModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  // Initial data loading when client ID changes
  useEffect(() => {
    if (id) {
      fetchClient(id);
    }
  }, [id, fetchClient]);

  // Load all tab data when client changes
  useEffect(() => {
    if (!client?._id) return;

    const fetchAllData = async () => {
      // Fetch quotes
      try {
        setLoadingQuotes(true);
        const quotesData = await quoteApi.getClientQuotes(client._id);
        setQuotes(quotesData);
      } catch (error) {
        
        toast.error('Failed to load quotes');
      } finally {
        setLoadingQuotes(false);
      }

      // Fetch services and suppliers
      try {
        setLoadingServices(true);
        const [servicesData, suppliersData] = await Promise.all([
          getClientUtilityServices(client._id),
          fetchSuppliers()
        ]);
        setServices(servicesData);
        setSuppliers(suppliersData || []);
      } catch (error) {
        
        toast.error('Failed to load services');
      } finally {
        setLoadingServices(false);
      }

      // Fetch subscribers - search by leadId and email
      try {
        setLoadingSubscribers(true);
        const response = await subscriberApi.getByLeadId(client._id, client.email);
        setSubscribers(response.data || []);
      } catch (error) {
        console.error('Failed to load subscribers:', error);
      } finally {
        setLoadingSubscribers(false);
      }
    };

    fetchAllData();
  }, [client?._id, client?.email]);

  // Refresh data when tab changes
  useEffect(() => {
    if (!client?._id) return;

    const refreshTabData = async () => {
      if (tabValue === 'quotes') {
        try {
          setLoadingQuotes(true);
          const data = await quoteApi.getClientQuotes(client._id);
          setQuotes(data);
        } catch (error) {
          
          toast.error('Failed to load quotes');
        } finally {
          setLoadingQuotes(false);
        }
      } else if (tabValue === 'services') {
        try {
          setLoadingServices(true);
          const [data, suppliersData] = await Promise.all([
            getClientUtilityServices(client._id),
            fetchSuppliers()
          ]);
          setServices(data);
          setSuppliers(suppliersData || []);
        } catch (error) {
          
          toast.error('Failed to load services');
        } finally {
          setLoadingServices(false);
        }
      } else if (tabValue === 'subscribers') {
        try {
          setLoadingSubscribers(true);
          const response = await subscriberApi.getByLeadId(client._id, client.email);
          setSubscribers(response.data || []);
        } catch (error) {
          console.error('Failed to load subscribers:', error);
        } finally {
          setLoadingSubscribers(false);
        }
      }
    };

    refreshTabData();
  }, [tabValue, client?._id, client?.email]);

  const handleViewDocument = async (document: Document) => {
    if (!document.url && !document.s3Key) return;
    
    try {
      // Generate a fresh pre-signed URL for viewing
      const response = await axios.get(`/clients/${client?._id}/documents/${document._id}/download`);
      
      if (response.data.success && response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        toast.error('Failed to generate document URL');
      }
    } catch (error) {
      console.error('Error generating fresh document URL:', error);
      toast.error('Failed to open document');
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!client?._id) return;

    try {
      await deleteDocument(client._id, documentId);
      toast.success('Document deleted successfully');
      await fetchClient(client._id); // Refresh client data
    } catch (error) {
      
      toast.error('Failed to delete document');
    }
  };

  const handleDocumentUpload = async (file: File) => {
    if (!client?._id) return;

    try {
      await uploadDocument(client._id, file, 'GENERAL');
      toast.success('Document uploaded successfully');
      await fetchClient(client._id); // Refresh client data to show new document
    } catch (error) {
      
      toast.error('Failed to upload document');
    }
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const handleEditClick = () => {
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
  };

  const handleSaveClient = async (updatedClient: UpdateClientDTO) => {
    try {
      if (client) {
        await updateClient(client._id, updatedClient);
        // Refresh client data to show updated information
        await fetchClient(client._id);
        setEditDialogOpen(false);
        setSnackbar({
          open: true,
          message: 'Client updated successfully',
          severity: 'success'
        });
      }
    } catch (error: any) {
      console.error('Error updating client:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update client. Please try again.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleViewQuote = (quoteId: string) => {
    navigate(`/utilities/quotes/${quoteId}/view`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, color: 'text.primary' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !client) {
    return (
      <Box sx={{ p: 3, color: 'text.primary' }}>
        <Typography color="error">
          {error || 'Client not found'}
        </Typography>
      </Box>
    );
  }

  // Calculate counts from loaded data
  const propertyCount = client.properties?.length || 0;
  // Use filtered document count if available, otherwise fall back to total count
  const documentCount = filteredDocumentCount || client.documents?.length || 0;
  const serviceCount = services?.length || 0;
  const quoteCount = quotes?.length || 0;
  const subscriberCount = subscribers?.length || 0;
  
  // Debug information
  console.log('Current tab value:', tabValue);
  console.log('Available tabs: properties, documents, services, quotes, notes');
  console.log('NotesPanel component available:', typeof NotesPanel === 'function');

  return (
    <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(12px)', borderRadius: 2 }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/utilities/clients')}
              sx={{
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              Back to Clients
            </Button>
          </Box>
          <Box>
            <Card sx={{ backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" component="h1" color="text.primary">
                    Client Details - {client.companyName}
                  </Typography>
                  <Box>
                    <Button
                      startIcon={<EditIcon />}
                      variant="outlined"
                      onClick={handleEditClick}
                      sx={{ mr: 1, borderColor: 'rgba(255, 255, 255, 0.2)' }}
                    >
                      Edit
                    </Button>
                    <IssueLOAButton client={client} onLOAIssued={() => fetchClient(client._id)} />
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  {/* Business Details Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ 
                      borderLeft: '4px solid #3b82f6', 
                      pl: 2,
                      py: 0.5,
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      borderRadius: '0 4px 4px 0',
                      color: 'text.primary'
                    }}>Business Details</Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
                          <Box>
                            <Typography variant="subtitle2" color="text.primary">Company Details</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{client.companyName}</Typography>
                              <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 16 }} />
                              <Typography variant="body2" color="text.secondary">{client.businessType}</Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                      
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
                          <Box>
                            <Typography variant="subtitle2" color="text.primary">Industry</Typography>
                            <Typography variant="body1" color="text.primary">{client.industry || 'Not specified'}</Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    {client.businessType === 'Limited Company' && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
                            <Box>
                              <Typography variant="subtitle2" color="text.primary">Company Number</Typography>
                              <Typography variant="body1" color="text.primary">{client.companyNumber}</Typography>
                            </Box>
                          </Box>
                        </Paper>
                        
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
                            <Box>
                              <Typography variant="subtitle2" color="text.primary">Incorporated Date</Typography>
                              <Typography variant="body1" color="text.primary">{client.incorporationDate ? new Date(client.incorporationDate).toLocaleDateString('en-GB') : 'Not available'}</Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Box>
                    )}
                  </Grid>
                  
                  {/* Contact Information Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ 
                      mt: 2,
                      borderLeft: '4px solid #10b981', 
                      pl: 2,
                      py: 0.5,
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      borderRadius: '0 4px 4px 0',
                      color: 'text.primary'
                    }}>Contact Information</Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                          <Box>
                            <Typography variant="subtitle2" color="text.primary">Client Liaison</Typography>
                            <Typography variant="body1" color="text.primary">{client.position ? `${client.ownerName} - ${client.position}` : client.ownerName}</Typography>
                          </Box>
                        </Box>
                      </Paper>
                      
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
                          <Box>
                            <Typography variant="subtitle2" color="text.primary">Email</Typography>
                            <Typography variant="body1" color="text.primary">{client.email}</Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
                          <Box>
                            <Typography variant="subtitle2" color="text.primary">Phone</Typography>
                            <Typography variant="body1" color="text.primary">{client.phoneNumber}</Typography>
                          </Box>
                        </Box>
                      </Paper>
                      
                      {(() => {
                        const isBirthday = client.dateOfBirth ? (() => {
                          const dob = new Date(client.dateOfBirth!);
                          const today = new Date();
                          return dob.getDate() === today.getDate() && dob.getMonth() === today.getMonth();
                        })() : false;
                        return (
                          <Paper elevation={1} sx={{
                            p: 2, borderRadius: 2, backdropFilter: 'blur(12px)',
                            backgroundColor: isBirthday ? 'rgba(245, 158, 11, 0.15)' : 'rgba(255, 255, 255, 0.1)',
                            border: isBirthday ? '1px solid rgba(245, 158, 11, 0.5)' : '1px solid rgba(255, 255, 255, 0.2)',
                            ...(isBirthday && {
                              animation: 'birthdayPulse 2s ease-in-out infinite',
                              '@keyframes birthdayPulse': {
                                '0%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.4)' },
                                '50%': { boxShadow: '0 0 12px 4px rgba(245, 158, 11, 0.3)' },
                                '100%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.4)' },
                              },
                            }),
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CakeIcon sx={{ mr: 2, color: isBirthday ? '#f59e0b' : 'primary.main', ...(isBirthday && { fontSize: 28 }) }} />
                              <Box>
                                <Typography variant="subtitle2" color="text.primary">
                                  Date of Birth {isBirthday && '🎂 Happy Birthday!'}
                                </Typography>
                                <Typography variant="body1" color="text.primary">{client.dateOfBirth ? new Date(client.dateOfBirth).toLocaleDateString('en-GB') : 'Not provided'}</Typography>
                              </Box>
                            </Box>
                          </Paper>
                        );
                      })()}
                    </Box>
                  </Grid>
                  
                  {/* Trading Information Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ 
                      mt: 2,
                      borderLeft: '4px solid #f59e0b', 
                      pl: 2,
                      py: 0.5,
                      backgroundColor: 'rgba(245, 158, 11, 0.1)',
                      borderRadius: '0 4px 4px 0',
                      color: 'text.primary'
                    }}>Trading Information</Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <LocationIcon sx={{ mr: 2, color: 'primary.main', mt: 0.5 }} />
                          <Box>
                            <Typography variant="subtitle2" color="text.primary">Trading Address</Typography>
                            <Typography variant="body1" color="text.primary">
                              {[
                                client.tradingAddress?.line1,
                                client.tradingAddress?.line2,
                                client.tradingAddress?.city,
                                client.tradingAddress?.county,
                                client.tradingAddress?.postcode
                              ].filter(Boolean).join(', ') || 'No address provided'}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                      
                      {client.businessType === 'Sole Trader' && (
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                            <Box>
                              <Typography variant="subtitle2" color="text.primary">Date of Birth</Typography>
                              <Typography variant="body1" color="text.primary">
                                {client.dateOfBirth ? new Date(client.dateOfBirth).toLocaleDateString('en-GB') : 'Not provided'}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      )}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {client.businessType === 'Limited Company' && client.registeredBusinessAddress && (
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <LocationIcon sx={{ mr: 2, color: 'primary.main', mt: 0.5 }} />
                            <Box>
                              <Typography variant="subtitle2" color="text.primary">Registered Address</Typography>
                              <Typography variant="body1" color="text.primary">
                                {[
                                  client.registeredBusinessAddress.line1,
                                  client.registeredBusinessAddress.line2,
                                  client.registeredBusinessAddress.city,
                                  client.registeredBusinessAddress.county,
                                  client.registeredBusinessAddress.postcode
                                ].filter(Boolean).join(', ') || 'No registered address provided'}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      )}
                      {client.homeAddress && (
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                              <LocationIcon sx={{ mr: 2, color: 'primary.main', mt: 0.5 }} />
                              <Box>
                                <Typography variant="subtitle2" color="text.primary">Home Address</Typography>
                                <Typography variant="body1" color="text.primary">
                                  {[
                                    client.homeAddress.line1,
                                    client.homeAddress.line2,
                                    client.homeAddress.city,
                                    client.homeAddress.county,
                                    client.homeAddress.postcode
                                ].filter(Boolean).join(', ') || 'No home address provided'}
                              </Typography>
                            </Box>
                            </Box>
                            <Button 
                              size="small" 
                              variant="outlined" 
                              onClick={() => setAddressHistoryModalOpen(true)}
                              sx={{ ml: 1, flexShrink: 0 }}
                            >
                              View History
                            </Button>
                          </Box>
                        </Paper>
                      )}
                      {client.businessType === 'Sole Trader' && (
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
                            <Box>
                              <Typography variant="subtitle2" color="text.primary">Trading Duration</Typography>
                              <Typography variant="body1" color="text.primary">
                                {client.yearsTrading !== undefined || client.monthsTrading !== undefined
                                  ? `${client.yearsTrading || 0} ${client.yearsTrading === 1 ? 'year' : 'years'}${client.monthsTrading ? ` and ${client.monthsTrading} ${client.monthsTrading === 1 ? 'month' : 'months'}` : ''}`
                                  : 'Not specified'
                                }
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      )}
                    </Box>
                  </Grid>
                  
                  {/* Internal Information Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ 
                      mt: 2,
                      borderLeft: '4px solid #8b5cf6', 
                      pl: 2,
                      py: 0.5,
                      backgroundColor: 'rgba(139, 92, 246, 0.1)',
                      borderRadius: '0 4px 4px 0',
                      color: 'text.primary'
                    }}>Internal Information</Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <AccountCircleIcon sx={{ mr: 2, color: 'primary.main', mt: 0.5 }} />
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="subtitle2" color="text.primary" sx={{ mb: 0.5 }}>Client Liaison</Typography>
                            {client.representativeId && typeof client.representativeId === 'object' && 'firstName' in client.representativeId ? (
                              <>
                                <Typography variant="body1" color="text.primary">
                                  {`${(client.representativeId as User).firstName} ${(client.representativeId as User).lastName}`}
                                  {(client.representativeId as User).subBrokerLevel && (
                                    <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary', fontWeight: 400 }}>
                                      | {(client.representativeId as User).subBrokerLevel}
                                    </Typography>
                                  )}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="body1" color="text.primary">
                                {client.representative?.name || 'Not Assigned'}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                      
                      {client.notes && (
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <DescriptionIcon sx={{ mr: 2, color: 'primary.main', mt: 0.5 }} />
                            <Box>
                              <Typography variant="subtitle2" color="text.primary">Comments</Typography>
                              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }} color="text.primary">{client.notes}</Typography>
                            </Box>
                          </Box>
                        </Paper>
                      )}
                    </Box>
                  </Grid>
                  
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', opacity: 0.7 }}>
                  {client.createdBy?.firstName
                    ? `Created by ${client.createdBy.firstName} ${client.createdBy.lastName} on ${new Date(client.createdAt).toLocaleDateString()}`
                    : `Created on ${new Date(client.createdAt).toLocaleDateString()}`
                  }
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ width: '100%', mt: 3 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.2)' }}>
              <Tabs
                value={tabValue}
                onChange={handleChange}
                aria-label="client tabs"
                sx={{
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'primary.main',
                  },
                  '& .MuiTab-root': {
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      color: 'primary.main',
                    },
                  },
                }}
              >
                <Tab label={`Properties (${propertyCount})`} value="properties" />
                <Tab label="Contacts" value="contacts" />
                <Tab label={`Documents (${documentCount})`} value="documents" />
                <Tab label={`Contracts (${serviceCount})`} value="services" />
                <Tab label={`Quotes (${quoteCount})`} value="quotes" />
                <Tab label={`Subscribers (${subscriberCount})`} value="subscribers" />
                <Tab label="Notes" value="notes" />
              </Tabs>
            </Box>

            {tabValue === 'properties' && (
              <Box sx={{ mt: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(`/utilities/clients/${client._id}/properties/new`)}
                    sx={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                      }
                    }}
                  >
                    Add Property
                  </Button>
                </Box>
                <PropertyList 
                  properties={client.properties || []}
                  onSelect={(property) => navigate(`/utilities/clients/${client._id}/properties/${property._id}`)}
                  clientId={client._id}
                />
              </Box>
            )}

            {tabValue === 'contacts' && (
              <ContactsPanel clientId={client._id} properties={client.properties || []} />
            )}

            {tabValue === 'documents' && (
              <Box sx={{ mt: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2, p: 2 }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <input
                    accept="*/*"
                    style={{ display: 'none' }}
                    id="client-document-upload"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleDocumentUpload(file);
                        e.target.value = ''; // Reset the input
                      }
                    }}
                  />
                  <label htmlFor="client-document-upload">
                    <Button
                      variant="contained"
                      component="span"
                      color="primary"
                      startIcon={<AddIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                        }
                      }}
                    >
                      Upload Document
                    </Button>
                  </label>
                </Box>
                <DocumentList
                  clientId={client._id}
                  documentType="client"
                  documents={client.documents || []}
                  onDocumentDeleted={() => fetchClient(client._id)}
                  onViewDocument={handleViewDocument}
                  onDeleteDocument={handleDeleteDocument}
                  onFilteredCountChange={setFilteredDocumentCount}
                  loading={loading}
                />
            </Box>
          )}

          {tabValue === 'services' && (
            <Box sx={{ mt: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/utilities/contracts/new?clientId=${client._id}`)}
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                    }
                  }}
                >
                  Add Contract
                </Button>
              </Box>
              {loadingServices ? (
                <Box display="flex" justifyContent="center" p={3} color="text.primary">
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Property</TableCell>
                        <TableCell>Contract Type</TableCell>
                        <TableCell>Supplier</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Contract End</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {services.map((service) => (
                        <TableRow 
                          key={service._id}
                          onClick={() => {
                            const propertyId = typeof service.property === 'object' ? service.property._id : service.property;
                            navigate(`/utilities/contracts/${service._id}?propertyId=${propertyId}&clientId=${client._id}`);
                          }}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
                          }}
                        >
                          <TableCell>
                            {service.property?.name || 'Unknown Property'}
                          </TableCell>
                          <TableCell>{formatServiceType(service.serviceType)}</TableCell>
                          <TableCell>
                            {(() => {
                              // Handle populated supplier object
                              if (typeof service.supplier === 'object' && service.supplier !== null && 'name' in service.supplier) {
                                return (service.supplier as any).name;
                              }
                              // Handle supplier ID - lookup in suppliers array
                              const supplierId = typeof service.supplier === 'string' ? service.supplier : (service.supplier as any)?._id;
                              return suppliers.find(s => s._id === supplierId)?.name || 'N/A';
                            })()}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={service.status}
                              color={service.status === 'LIVE' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {service.contractEndDate ? new Date(service.contractEndDate).toLocaleDateString() : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {services.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary">
                              No contracts found. Click the "Add Contract" button to create a new contract.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {tabValue === 'quotes' && (
            <Box sx={{ mt: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/utilities/quotes/new?clientId=${client._id}`)}
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                    }
                  }}
                >
                  Add Quote
                </Button>
              </Box>
              <QuoteList 
                quotes={quotes}
                clientId={client._id}
                onViewQuote={handleViewQuote}
                showPropertyName={true}
                showClientName={false}
                loading={loadingQuotes}
              />
            </Box>
          )}
          
          {tabValue === 'subscribers' && (
            <Box sx={{ mt: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2 }}>
              {loadingSubscribers ? (
                <Box display="flex" justifyContent="center" p={3} color="text.primary">
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Tags</TableCell>
                        <TableCell>Subscribed</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subscribers.map((subscriber) => (
                        <TableRow key={subscriber._id}>
                          <TableCell>{subscriber.email}</TableCell>
                          <TableCell>
                            {subscriber.firstName || subscriber.lastName
                              ? `${subscriber.firstName || ''} ${subscriber.lastName || ''}`.trim()
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={subscriber.status || 'ACTIVE'}
                              color={subscriber.status === 'ACTIVE' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {subscriber.tags && subscriber.tags.length > 0
                              ? subscriber.tags.map((tag) => (
                                  <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                ))
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {subscriber.createdAt
                              ? new Date(subscriber.createdAt).toLocaleDateString()
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {subscribers.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary">
                              No subscribers linked to this client.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {tabValue === 'notes' && (
            <Box sx={{ mt: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2, p: 2 }}>
              <NotesPanel 
                entityType="client" 
                entityId={client._id} 
                title="Client Notes"
              />
            </Box>
          )}
        </Box>
      </Box>
      </Container>
      
      {/* Address History Modal */}
      {client && (
        <AddressHistoryModal
          open={addressHistoryModalOpen}
          onClose={() => setAddressHistoryModalOpen(false)}
          client={client}
        />
      )}
      
      <EditClientDialog
        open={editDialogOpen}
        onClose={handleEditClose}
        client={client}
        onSave={handleSaveClient}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientDetails;
